import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';

import { ConfirmDialogComponent } from '@app/core/components/confirm-dialog/confirm-dialog.component';
import { ISelect } from '@app/models/select.model';
import { RoleType, UserType } from '@app/models/user.model';
import { ToasterService } from '@app/services/toaster.service';
import { UserService } from '@app/services/user.service';
import { environment } from '@env/environment';
import { FormValidatorService } from '../../../services/form.validator.service';

@Component({
    selector: 'app-new-internal-user-modal',
    templateUrl: './new-internal-user-modal.component.html',
    styleUrls: ['./new-internal-user-modal.component.scss'],
    standalone: false
})
export class NewInternalUserModalComponent implements OnInit {
  userForm!: FormGroup;
  editMode!: boolean;
  adminUsers!: boolean;
  firstName!: string;
  lastName!: string;
  lastLogin!: string;
  fileName: string = 'Not picture selected';
  previewUrl: string | ArrayBuffer | null = null;
  roles: ISelect[] = [
    {
      id: 1,
      value: RoleType.ADMIN,
    },
    {
      id: 2,
      value: RoleType.USER,
    },
  ];
  carrierId: number = environment.carrierId;
  isDisabled: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<NewInternalUserModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private userService: UserService,
    private toasterService: ToasterService,
    private dialog: MatDialog,
    private formValidator: FormValidatorService
  ) {
    if (data.adminUsers) this.adminUsers = data.adminUsers;

    const formControls: Record<string, any> = {
      firstName: [data.FirstName, [Validators.required, formValidator.noSpacesValidator(), formValidator.noSpecialCharsValidator()]],
      lastName: [data.LastName, [Validators.required, formValidator.noSpacesValidator(), formValidator.noSpecialCharsValidator()]],
      email: [data.Email, [Validators.required, Validators.email]],
      //password: ['', [Validators.required]],
      avatar: [null],
    };
    const role =
      data.UserType === 'shipper admin' ? RoleType.ADMIN : RoleType.USER;
    formControls['role'] = [{ value: role, disabled: true }, [Validators.required]];
    this.userForm = this.fb.group(formControls);

    this.editMode = data.editMode;
    this.firstName = data.FirstName;
    this.lastName = data.LastName;
    this.lastLogin = data.lastLogin;
  }

  ngOnInit(): void {
    this.userForm.get('firstName')?.valueChanges.subscribe((value) => {
      this.firstName = value;
    });
    this.userForm.get('lastName')?.valueChanges.subscribe((value) => {
      this.lastName = value;
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.isDisabled = true;
    const request: any = {
      FirstName: this.userForm.get('firstName')?.value,
      LastName: this.userForm.get('lastName')?.value,
      Email: this.userForm.get('email')?.value,
      UserType:
        this.userForm.get('role')?.value === RoleType.USER
          ? UserType.CARRIER_USER
          : UserType.CARRIER_ADMIN,
      CarrierId: this.data.carrierId,
    };

    if (this.data?.ObjectId) {
      request.ObjectId = this.data.ObjectId;
    }
    if (this.data?.PrincipalName) {
      request.PrincipalName = this.data.PrincipalName;
    }
    this.userService.upsertCarrierUser(request).subscribe({
      next: () => {
        this.toasterService.showSuccess(
          `The Internal User was ${
            this.editMode === true ? 'edited' : 'created'
          } successfully`
        );
        this.dialogRef.close(this.userForm.getRawValue());
      },
      error: (error) => {
        this.isDisabled = false;
        const errorMessage = error.error.error;
        this.toasterService.showError(`Error Creating User: ${errorMessage}`);
        console.log('Error object:', error);
      },
    });
  }

  onDelete(): void {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to delete the Internal User?',
      },
      id: 'confirm-dialog',
    });

    confirmDialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userService.deleteCarrierUser(this.data.ObjectId).subscribe({
          next: () => {
            this.toasterService.showSuccess(
              'The Internal User was deleted successfully'
            );
            this.dialogRef.close(null);
          },
          error: (error) => {
            console.log(error);
          },
        });
      }
    });
  }

  isOnSaveDisabled() {
    return !this.userForm.valid || this.isDisabled;
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files![0];
    if (file) {
      this.fileName = file.name;
      this.userForm.patchValue({ avatar: file });
      this.userForm.get('avatar')!.updateValueAndValidity();

      const reader = new FileReader();
      reader.onload = () => {
        this.previewUrl = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }
}
